import React, { useMemo, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import * as XLSX from "xlsx";
import { MaterialReactTable } from "material-react-table";
import { Link } from "react-router-dom";
import { decodeHtml } from "../../../utils/decodeHtml";
import { currency_converter_inr } from "../../../utils/currencyUtils";
ChartJS.register(ArcElement, Tooltip, Legend);

const centerTextPlugin = {
  id: "centerTextPlugin",
  beforeDraw(chart, args, pluginOptions) {
    const { ctx, width, height } = chart;

    ctx.save();

    const clientCount = pluginOptions.clientCount || 0;

    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    ctx.font = "bold 22px Arial";
    ctx.fillStyle = "#212529";
    ctx.fillText(clientCount, width / 2, height / 2 - 6);

    ctx.font = "14px Arial";
    ctx.fillStyle = "#6c757d";
    ctx.fillText("Members", width / 2, height / 2 + 14);

    ctx.restore();
  },
};

const RevenueOutstandingMember = ({
  chart_data = [],
  outstandingData = [],
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState("");
  const [selectedInvoices, setSelectedInvoices] = useState([]);

  // --------------------------------------------------
  // Modal table columns
  // IMPORTANT: Hook must be before early return
  // --------------------------------------------------
  const modalColumns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        size: 150,
        Cell: ({ row }) => (
          <span>
            {decodeHtml(row.original.name || "")}
          </span>
        ),
      },
      {
        accessorKey: "role_name",
        header: "Role Name",
        size: 150,
        Cell: ({ row }) => (
          <span>
            {decodeHtml(row.original.role_name || "")}
          </span>
        ),
      },
      {
        accessorKey: "company_name",
        header: "Client",
        size: 150,
      },
      {
        accessorKey: "assignedTo",
        header: "Assigned To",
        size: 150,
      },
      {
        accessorKey: "roleManager",
        header: "Role Manager",
        size: 150,
      },
      {
        accessorKey: "entity",
        header: "Entity",
        size: 100,
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 100,
      },
      {
        accessorKey: "invoice_number",
        header: "Invoice Number",
        size: 140,
      },
      {
        accessorKey: "raised_date",
        header: "Raised On",
        size: 120,
      },
      {
        accessorKey: "issue_date",
        header: "Issued On",
        size: 120,
      },
      {
        accessorKey: "due_date",
        header: "Due Date",
        size: 120,
      },
      {
        accessorKey: "major_industry",
        header: "Industry",
        size: 130,
      },
      {
        accessorKey: "region",
        header: "Region",
        size: 100,
      },
      {
        accessorKey: "amount",
        header: "Amount",
        size: 120,
      },
      {
        accessorKey: "revenue",
        header: "Total (INR)",
        size: 130,
        Cell: ({ row }) =>
          currency_converter_inr(row.original.revenue),
      },
    ],
    []
  );

  // Early return AFTER all hooks
  if (!Array.isArray(chart_data) || chart_data.length === 0) {
    return <div>No data available</div>;
  }

  const COLOR_PALETTE = [
    "#EDC948",
    "#F28E2B",
    "#B07AA1",
    "#76B7B2",
    "#E15759",
    "#59A14F",
    "#4E79A7",
    "#FF9DA7",
    "#9C755F",
    "#BAB0AC",
  ];

  const TOP_N = 3;

  const sortedData = [...chart_data].sort(
    (a, b) =>
      Number(b.total_amount) - Number(a.total_amount)
  );

  const topClients = sortedData.slice(0, TOP_N);
  const others = sortedData.slice(TOP_N);

  const othersTotal = others.reduce(
    (sum, item) => sum + Number(item.total_amount),
    0
  );

  const chartClients =
    othersTotal > 0
      ? [
          ...topClients,
          {
            member_name: "Others",
            total_amount: othersTotal,
          },
        ]
      : topClients;

  const labels = chartClients.map(
    (item) => item.member_name
  );

  const values = chartClients.map((item) =>
    Number(Number(item.total_amount).toFixed(2))
  );

  const selectedMemberRevenue =
    chart_data.find(
      (item) =>
        String(item.member_name).trim().toLowerCase() === String(selectedMember).trim().toLowerCase()
    )?.total_amount || 0;

  const colors = labels.map(
    (_, index) =>
      COLOR_PALETTE[index % COLOR_PALETTE.length]
  );

  // --------------------------------------------------
  // Open member outstanding modal
  // --------------------------------------------------
const handleMemberClick = (memberName) => {
  const selectedName = String(memberName).trim().toLowerCase();

  let filteredData = [];

  if (memberName === "Others") {
    const topMemberNames = topClients.map((item) =>String(item.member_name).trim().toLowerCase());
    filteredData = outstandingData.filter((item) => {
      const assignedTo = String(item.assignedTo || item.assigned_to || "").trim().toLowerCase();
      const roleManager = String(item.roleManager || item.role_manager || "").trim().toLowerCase();
      // Include invoice if neither Assigned To
      // nor Role Manager belongs to Top 3
      return (
        !topMemberNames.includes(assignedTo) &&
        !topMemberNames.includes(roleManager)
      );
    });
  } else {
    filteredData = outstandingData.filter((item) => {
      const assignedTo = String(item.assignedTo || item.assigned_to || "").trim().toLowerCase();
      const roleManager = String(item.roleManager || item.role_manager || "").trim().toLowerCase();
      // Match if member is either Assigned To OR Role Manager
      return ( assignedTo === selectedName || roleManager === selectedName);
    });
  }

  setSelectedMember(memberName);
  setSelectedInvoices(filteredData);
  setShowModal(true);
};

  // --------------------------------------------------
  // Modal table columns
  // --------------------------------------------------
  const data = {
    labels,
    datasets: [
      {
        label: "Outstanding",
        data: values,
        backgroundColor: colors,
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "55%",
    plugins: {
      legend: {
        display: false,
        position: "bottom",
      },
      tooltip: {
        callbacks: {
          label: (ctx) =>
            `₹ ${ctx.parsed.toFixed(2)} Cr`,
        },
      },
    },

    // Click on doughnut segment
    onClick: (event, elements) => {
      if (!elements.length) return;

      const index = elements[0].index;
      const memberName = labels[index];

      handleMemberClick(memberName);
    },
  };

  const handleDownloadExcel = () => {
  if (!selectedInvoices.length) {
    return;
  }

  const excelData = selectedInvoices.map((item) => ({
      Name: decodeHtml(item.name || ""),
      "Role Name": decodeHtml(item.role_name || ""),
      Client: item.company_name || "",
      "Assigned To": item.assignedTo || "",
      "Role Manager": item.roleManager || "",
      Entity: item.entity || "",
      Status: item.status || "",
      "Invoice Number": item.invoice_number || "",
      "Raised On": item.raised_date || "",
      "Issued On": item.issue_date || "",
      "Due Date": item.due_date || "",
      Industry: item.major_industry || "",
      Region: item.region || "",
      Amount: item.amount || "",
      "Total (INR)": item.revenue || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Outstanding"
    );

    const safeMemberName = selectedMember
      .replace(/[^a-zA-Z0-9]/g, "_")
      .substring(0, 50);

    XLSX.writeFile(
      workbook,
      `Outstanding_${safeMemberName}.xlsx`
    );
  };

  return (
    <>
      <div className="d-flex flex-column h-100">

        {/* Doughnut */}
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: 170 }}
        >
          <div style={{ width: 170 }}>
            <Doughnut
              data={data}
              plugins={[centerTextPlugin]}
              options={{
                ...options,
                plugins: {
                  ...options.plugins,
                  centerTextPlugin: {
                    clientCount: chart_data.length,
                  },
                },
              }}
            />
          </div>
        </div>

        {/* Member List */}
        <div
          className="mt-2 flex-grow-1 overflow-auto"
          style={{ minHeight: 0 }}
        >
          <table className="table table-sm mb-0">
            <tbody>
              {chart_data.map((item, index) => (
                <tr
                  key={index}
                  onClick={() =>
                    handleMemberClick(item.member_name)
                  }
                  style={{
                    cursor: "pointer",
                  }}
                  title={`View outstanding invoices for ${item.member_name}`}
                >
                  <td width="20">
                    <span
                      style={{
                        width: 12,
                        height: 12,
                        display: "inline-block",
                        borderRadius: "50%",
                        ...(index < 3 && {
                          backgroundColor: colors[index],
                        }),
                      }}
                    />
                  </td>

                  <td className="text-truncate">
                    {item.member_name}
                  </td>

                  <td className="text-end fw-semibold">
                    ₹{" "}
                    {Number(item.total_amount).toFixed(2)} Cr
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* =====================================================
          Outstanding Member Modal
         ===================================================== */}
      {showModal && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.5)",
          }}
          tabIndex="-1"
          role="dialog"
        >
          <div
            className="modal-dialog modal-xl modal-dialog-centered"
            style={{ maxWidth: "95%" }}
            role="document"
          >
            <div className="modal-content">

              {/* Modal Header */}
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-1">
                    Outstanding Invoices
                  </h5>

                  <div className="text-muted">
                    Team Member:{" "}
                    <strong>{selectedMember}</strong>

                    <span className="ms-3 fw-semibold text-dark">
                      ₹ {Number(selectedMemberRevenue).toFixed(2)} Cr
                    </span>
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={handleDownloadExcel}
                    disabled={!selectedInvoices.length}
                  >
                    <i className="fa fa-download me-1" />
                    Download Excel
                  </button>

                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowModal(false)}
                  />
                </div>
              </div>

              {/* Modal Body */}
              <div className="modal-body">

                {selectedInvoices.length > 0 ? (
                  <MaterialReactTable
                    columns={modalColumns}
                    data={selectedInvoices}
                    enableGrouping
                    enablePagination
                    enableColumnFilters
                    enableSorting
                    muiTableBodyRowProps={({ row }) => ({
                      onClick: () => {
                        if (row.original.invoice_id) {
                          window.open(
                            `/order/view/${row.original.invoice_id}`,
                            "_blank",
                            "noopener,noreferrer"
                          );
                        }
                      },
                      sx: {
                        cursor: "pointer",
                      },
                    })}
                    initialState={{
                      density: "compact",
                      pagination: {
                        pageIndex: 0,
                        pageSize: 100,
                      },
                    }}
                  />
                ) : (
                  <div className="text-center py-5 text-muted">
                    No outstanding invoices found for{" "}
                    <strong>{selectedMember}</strong>.
                  </div>
                )}

              </div>

              {/* Modal Footer */}
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Modal backdrop */}
      {showModal && (
        <div
          className="modal-backdrop fade show"
          onClick={() => setShowModal(false)}
        />
      )}
    </>
  );
};

export default RevenueOutstandingMember;
